import { useEffect } from 'react';
import { useWidgetAllowed, loadWidget, destroyWidget } from '@/lib/freshdesk';

/**
 * SAB-938 — loads the Freshdesk web chat widget, and only after consent is recorded.
 *
 * ⚠️ THIS COMPONENT IS THE REASON NOTHING FRESHDESK-RELATED IS FETCHED ON A FIRST VISIT.
 * Before it existed, an inline script in index.html bound to `window.load` and ran
 * unconditionally: 29 requests to Freshdesk/Freshworks/AWS completed before the visitor had
 * answered the cookie banner, a visitor record was created, and a durable identifier was
 * written to localStorage on the freshdesk.com origin. Deleting that script without putting
 * this in its place would simply remove support chat; deleting this without restoring a gate
 * would silently reintroduce the whole finding.
 *
 * Mounted once at the App root, alongside FreshdeskIdentity, so its lifetime is the
 * document's — which is what the old `window.load` binding was really buying. The script-id
 * guard in loadWidget() makes a second mount a no-op rather than a second launcher.
 *
 * Renders nothing. All of its behaviour is the effect below.
 */
const FreshdeskLoader = () => {
  const allowed = useWidgetAllowed();

  useEffect(() => {
    if (allowed) {
      loadWidget();
      return;
    }
    // Withdrawal (resetConsent, or Customize with analytics turned off). Best-effort by
    // design: this stops every future request and removes the frame, but the token on the
    // freshdesk.com origin is cross-origin and cannot be cleared from here. See the note on
    // destroyWidget() — and the disclosure copy says "stops collection", not "erases".
    destroyWidget();
  }, [allowed]);

  return null;
};

export default FreshdeskLoader;
